/**
 * API client for live tracking data
 * Infrastructure layer for HTTP communication
 */

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://api.sledzgps.pl/api/v1'

/**
 * HTTP client with error handling
 */
class ApiClient {
  async get(url) {
    try {
      const response = await fetch(url)
      
      if (!response.ok) {
        throw new ApiError(response.status, await this._parseErrorMessage(response))
      }
      
      return await response.json()
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'Błąd połączenia z serwerem. Sprawdź połączenie internetowe.')
      }
      
      throw new ApiError(500, `Nieoczekiwany błąd: ${error.message}`)
    }
  }

  async _parseErrorMessage(response) {
    try {
      const errorData = await response.json()
      return errorData.message || this._getDefaultErrorMessage(response.status)
    } catch {
      return this._getDefaultErrorMessage(response.status)
    }
  }

  _getDefaultErrorMessage(status) {
    switch (status) {
      case 404:
        return 'Nie znaleziono zasobu'
      case 422:
        return 'Nieprawidłowe parametry zapytania'
      case 500:
        return 'Błąd serwera'
      default:
        return `Błąd serwera: ${status}`
    }
  }
}

/**
 * Custom API error class
 */
export class ApiError extends Error {
  constructor(status, message) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }

  get isNotFound() {
    return this.status === 404
  }

  get isValidationError() {
    return this.status === 422
  }

  get isServerError() {
    return this.status >= 500
  }

  get isNetworkError() {
    return this.status === 0
  }
}

/**
 * Event data API client
 */
export class EventApiClient extends ApiClient {
  /**
   * Fetch event data including markers and route URL
   * @param {string} eventAlias - The event alias/name
   * @returns {Promise<Object>} Event data from API
   */
  async getEventData(eventAlias) {
    const url = `${API_BASE_URL}/events/alias/${eventAlias}`
    
    try {
      return await this.get(url)
    } catch (error) {
      if (error.isNotFound) {
        throw new ApiError(404, `Nie znaleziono wydarzenia ${eventAlias}`)
      }
      throw error
    }
  }
}

/**
 * Runner tracking data API client
 */
export class RunnerApiClient extends ApiClient {
  /**
   * Fetch runner data from the live API
   * @param {string} eventAlias - The event alias/name
   * @param {string|number} runnerNumber - The runner's bib number
   * @param {Object} options - Optional query parameters
   * @returns {Promise<Object>} Runner data from API
   */
  async getRunnerData(eventAlias, runnerNumber, options = {}) {
    const queryParams = new URLSearchParams()
    
    // Add optional query parameters
    if (options.includeTracks !== undefined) {
      queryParams.append('includeTracks', options.includeTracks)
    }
    if (options.includeSplits !== undefined) {
      queryParams.append('includeSplits', options.includeSplits)
    }
    if (options.limit) {
      queryParams.append('limit', options.limit)
    }
    if (options.offset) {
      queryParams.append('offset', options.offset)
    }
    if (options.source) {
      queryParams.append('source', options.source)
    }
    
    const queryString = queryParams.toString()
    const url = `${API_BASE_URL}/events/${eventAlias}/runners/${runnerNumber}${queryString ? '?' + queryString : ''}`
    
    try {
      return await this.get(url)
    } catch (error) {
      if (error.isNotFound) {
        throw new ApiError(404, `Nie znaleziono zawodnika ${runnerNumber} w wydarzeniu ${eventAlias}`)
      }
      throw error
    }
  }
}

// Export singleton instances
export const eventApiClient = new EventApiClient()
export const runnerApiClient = new RunnerApiClient()